/**
 * Aruba users store — list + create + deactivate.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, patch as apiPatch, ApiError } from "@/api/client";
import {
  ArubaUserListSchema,
  type ArubaUser,
  type ArubaUserCreate,
} from "@/api/schemas/aruba";
import { MOCK_ARUBA_USERS } from "@/api/fixtures/aruba-users";

interface State {
  data: ArubaUser[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  create: (payload: ArubaUserCreate) => Promise<void>;
  setActive: (email: string, active: boolean) => Promise<void>;
}

export const useArubaUsers = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/aruba/users/");
      set({ data: ArubaUserListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_ARUBA_USERS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  create: async (payload) => {
    await apiPost("/aruba/users/", payload);
    await get().load();
  },

  setActive: async (email, active) => {
    // Optimistic flip — API confirmation reconciles via load().
    set((s) => ({ data: s.data.map((u) => (u.email === email ? { ...u, active } : u)) }));
    try {
      await apiPatch(`/aruba/users/${encodeURIComponent(email)}/`, { active });
    } finally {
      await get().load();
    }
  },
}));
